import { getComponent } from "../data/catalog";
import { getMap, getRack } from "../data/maps";
import { getScenario } from "../data/scenarios";
import { validatePlacement } from "./placement";
import { applyRackSettingsToMap } from "./rack-settings";
import {
    CATALOG_VERSION,
    type ImportResult,
    type MapId,
    type Placement,
    type Plan,
    type RackSettings,
    UTP_FORMAT,
    UTP_VERSION,
    type UtpDocument,
} from "./types";

const MAX_FILE_BYTES = 2_000_000;
const MAX_PLACEMENTS = 10_000;
const MAX_RACK_SETTINGS = 1_000;
const SAFE_ID = /^[a-zA-Z0-9][a-zA-Z0-9._:-]{0,127}$/;

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isValidIsoDate(value: unknown): value is string {
    return typeof value === "string" && value.length <= 40 && !Number.isNaN(Date.parse(value));
}

function readPlacement(value: unknown, index: number): Placement | string {
    if (!isRecord(value)) {
        return `Placement ${index + 1} must be an object.`;
    }
    const { id, componentId, rackId, startU, localName } = value;
    if (typeof id !== "string" || !SAFE_ID.test(id)) {
        return `Placement ${index + 1} has an invalid ID.`;
    }
    if (typeof componentId !== "string" || getComponent(componentId) === undefined) {
        return `Placement ${index + 1} references an unknown component ID.`;
    }
    if (typeof rackId !== "string" || !SAFE_ID.test(rackId)) {
        return `Placement ${index + 1} has an invalid rack ID.`;
    }
    if (!Number.isInteger(startU) || typeof startU !== "number") {
        return `Placement ${index + 1} has an invalid start U.`;
    }
    if (localName !== undefined && (typeof localName !== "string" || localName.length > 80)) {
        return `Placement ${index + 1} has an invalid local name.`;
    }
    return localName === undefined
        ? { id, componentId, rackId, startU }
        : { id, componentId, rackId, startU, localName };
}

function readRackSettings(value: unknown, index: number, mapId: MapId): RackSettings | string {
    if (!isRecord(value)) {
        return `Rack setting ${index + 1} must be an object.`;
    }
    const { rackId, name, showNameAsComponentPrefix, sizeU, sizeSource } = value;
    const map = getMap(mapId);
    if (
        typeof rackId !== "string" ||
        !SAFE_ID.test(rackId) ||
        map === undefined ||
        getRack(map, rackId) === undefined
    ) {
        return `Rack setting ${index + 1} references an unknown rack ID.`;
    }
    if (name !== undefined && (typeof name !== "string" || name.trim() === "" || name.length > 80)) {
        return `Rack setting ${index + 1} has an invalid rack name.`;
    }
    if (typeof showNameAsComponentPrefix !== "boolean") {
        return `Rack setting ${index + 1} has an invalid component-prefix option.`;
    }
    if (sizeU !== undefined && sizeU !== 24 && sizeU !== 42) {
        return `Rack setting ${index + 1} has an invalid rack height.`;
    }
    if (sizeSource !== undefined && sizeSource !== "api" && sizeSource !== "savegame") {
        return `Rack setting ${index + 1} has an invalid rack-height source.`;
    }
    return {
        rackId,
        ...(typeof name === "string" ? { name: name.trim() } : {}),
        showNameAsComponentPrefix,
        ...(sizeU === 24 || sizeU === 42 ? { sizeU } : {}),
        ...(sizeSource === "api" || sizeSource === "savegame" ? { sizeSource } : {}),
    };
}

export function serializePlan(plan: Plan, updatedAt = new Date().toISOString()): UtpDocument {
    const map = getMap(plan.project.mapId);
    if (map === undefined) {
        throw new Error("Cannot serialize a plan with an unknown map.");
    }
    return {
        format: UTP_FORMAT,
        version: UTP_VERSION,
        catalogVersion: CATALOG_VERSION,
        project: { ...plan.project, updatedAt },
        placements: plan.placements.map((placement) => ({ ...placement })),
        rackSettings: plan.rackSettings.map((settings) => ({ ...settings })),
    };
}

export function migrateUtp(value: unknown): unknown {
    if (isRecord(value) && isRecord(value.project) && value.project.scenarioId === undefined) {
        return { ...value, project: { ...value.project, scenarioId: "sandbox" } };
    }
    return value;
}

export function importPlanJson(source: string): ImportResult {
    if (new TextEncoder().encode(source).length > MAX_FILE_BYTES) {
        return { ok: false, error: "The file exceeds the 2 MB import limit." };
    }
    let parsed: unknown;
    try {
        parsed = migrateUtp(JSON.parse(source));
    } catch {
        return { ok: false, error: "The selected file is not valid JSON." };
    }
    if (!isRecord(parsed)) {
        return { ok: false, error: "The plan document must be a JSON object." };
    }
    if (parsed.format !== UTP_FORMAT) {
        return { ok: false, error: "This is not an Uptime datacenter plan." };
    }
    if (parsed.version !== UTP_VERSION) {
        return { ok: false, error: `Unsupported .utp format version: ${String(parsed.version)}.` };
    }
    if (parsed.catalogVersion !== CATALOG_VERSION) {
        return { ok: false, error: `Incompatible catalog version: ${String(parsed.catalogVersion)}.` };
    }
    if (!isRecord(parsed.project)) {
        return { ok: false, error: "Project metadata is missing." };
    }
    const { id, name, mapId, scenarioId, createdAt, updatedAt } = parsed.project;
    const map = typeof mapId === "string" ? getMap(mapId) : undefined;
    if (map === undefined) {
        return { ok: false, error: `Unknown map: ${String(mapId)}.` };
    }
    if (
        typeof id !== "string" ||
        !SAFE_ID.test(id) ||
        typeof name !== "string" ||
        name.trim() === "" ||
        name.length > 80
    ) {
        return { ok: false, error: "Project ID or name is invalid." };
    }
    if (!isValidIsoDate(createdAt) || !isValidIsoDate(updatedAt)) {
        return { ok: false, error: "Project timestamps are invalid." };
    }
    const scenario = typeof scenarioId === "string" ? getScenario(scenarioId) : undefined;
    if (scenario === undefined || !scenario.mapIds.includes(map.id)) {
        return { ok: false, error: `Unknown or incompatible scenario: ${String(scenarioId)}.` };
    }
    if (!Array.isArray(parsed.placements) || parsed.placements.length > MAX_PLACEMENTS) {
        return { ok: false, error: "Placements must be an array with at most 10,000 items." };
    }
    const rawRackSettings = parsed.rackSettings ?? [];
    if (!Array.isArray(rawRackSettings) || rawRackSettings.length > MAX_RACK_SETTINGS) {
        return { ok: false, error: "Rack settings must be an array with at most 1,000 items." };
    }

    const rackSettings: RackSettings[] = [];
    const rackSettingIds = new Set<string>();
    for (let index = 0; index < rawRackSettings.length; index += 1) {
        const settings = readRackSettings(rawRackSettings[index], index, map.id as MapId);
        if (typeof settings === "string") {
            return { ok: false, error: settings };
        }
        if (rackSettingIds.has(settings.rackId)) {
            return { ok: false, error: `Duplicate rack setting ID: ${settings.rackId}.` };
        }
        rackSettingIds.add(settings.rackId);
        rackSettings.push(settings);
    }

    const placements: Placement[] = [];
    const ids = new Set<string>();
    const configuredMap = applyRackSettingsToMap(map, rackSettings);
    for (let index = 0; index < parsed.placements.length; index += 1) {
        const placement = readPlacement(parsed.placements[index], index);
        if (typeof placement === "string") {
            return { ok: false, error: placement };
        }
        if (ids.has(placement.id)) {
            return { ok: false, error: `Duplicate placement ID: ${placement.id}.` };
        }
        ids.add(placement.id);
        if (getRack(configuredMap, placement.rackId) === undefined) {
            return { ok: false, error: `Unknown rack ID: ${placement.rackId}.` };
        }
        const result = validatePlacement(
            configuredMap,
            placements,
            placement.componentId,
            placement.rackId,
            placement.startU,
        );
        if (!result.valid) {
            return { ok: false, error: `Invalid placement ${placement.id}: ${result.reason ?? "validation failed"}` };
        }
        placements.push(placement);
    }
    return {
        ok: true,
        plan: {
            project: {
                id,
                name: name.trim(),
                mapId: map.id as MapId,
                scenarioId: scenario.id,
                createdAt,
                updatedAt,
            },
            placements,
            rackSettings,
        },
    };
}

export function getSafeFilename(name: string): string {
    const normalized = name
        .normalize("NFKD")
        .replace(/[^a-zA-Z0-9-_ ]/g, "")
        .trim()
        .replace(/\s+/g, "-")
        .toLowerCase()
        .slice(0, 60);
    return `${normalized || "uptime-plan"}.utp`;
}
